// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type HomeStoreEvents, useWhatsHappeningQuery } from "@/api/hooks/use-home";
import { DateValue } from "@/components/shared/datetime-value";
import { PanelColumns, PanelError, PanelHeader } from "@/components/shared/panel";
import { StoreLink } from "@/components/shared/store-link";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { storesListSearch } from "@/features/stores/stores-list-filters";
import { cn } from "@/lib/utils";

/** Relative day label: "Today", "In 2 days", "3 days ago". */
function RelativeDay({ days }: { days: number }) {
  const { t } = useTranslation("back-office");
  if (days === 0) return <Badge>{t("home.whatsHappening.today")}</Badge>;
  return (
    <span className={cn("text-xs", days < 0 && "text-muted-foreground")}>
      {days > 0 ? t("home.whatsHappening.inDays", { count: days }) : t("home.whatsHappening.daysAgo", { count: -days })}
    </span>
  );
}

interface StoreEventsPanelProps {
  title: string;
  description: string;
  emptyText: string;
  events: HomeStoreEvents | undefined;
  /** Stores-list search showing the whole window, for the "more" link. */
  moreSearch: (events: HomeStoreEvents) => { f?: string };
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRefresh: () => void;
}

/** One panel, laid out like a store scorecard panel: header, then a table of stores. */
function StoreEventsPanel({
  title,
  description,
  emptyText,
  events,
  moreSearch,
  isLoading,
  isError,
  isFetching,
  onRefresh,
}: StoreEventsPanelProps) {
  const { t } = useTranslation("back-office");

  return (
    <section className="space-y-3">
      <PanelHeader title={title} description={description} onRefresh={onRefresh} isRefreshing={isFetching} />

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-4/5" />
        </div>
      )}

      {isError && <PanelError onRetry={onRefresh} isRetrying={isFetching} />}

      {events && (
        <>
          <Table>
            <TableHeader>
              <TableRow className="bg-sidebar hover:bg-sidebar">
                <TableHead>{t("home.whatsHappening.columns.store")}</TableHead>
                <TableHead className="w-px text-right">{t("home.whatsHappening.columns.date")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.stores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-muted-foreground text-sm">
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                events.stores.map((event) => (
                  <TableRow key={event.storeName}>
                    <TableCell className="text-sm">
                      <StoreLink storeName={event.storeName} legibleName={event.legibleName} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <div className="flex flex-col items-end gap-0.5">
                        <DateValue value={event.date} className="text-sm" />
                        <RelativeDay days={event.daysFromToday} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Only the first few are listed; the rest open in the stores list, same window. */}
          {events.total > events.stores.length && (
            <div className="flex justify-end">
              <Link
                to="/stores"
                search={moreSearch(events)}
                className="inline-flex items-center gap-1 text-primary text-sm hover:underline"
              >
                {t("home.whatsHappening.more", { count: events.total - events.stores.length })}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/** Home page "What's happening": stores installing and going live around today. */
export function WhatsHappening() {
  const { t } = useTranslation("back-office");
  // One request feeds both panels, so either panel's refresh reloads both.
  const { data, isLoading, isError, isFetching, refetch } = useWhatsHappeningQuery();
  const shared = { isLoading, isError, isFetching, onRefresh: () => void refetch() };
  // "More" links turn the list's active-only default off: the panels list inactive stores too.

  return (
    <section className="space-y-4">
      <h3 className="font-semibold text-lg">{t("home.whatsHappening.title")}</h3>
      <PanelColumns
        columns={[
          [
            <StoreEventsPanel
              key="installs"
              title={t("home.whatsHappening.installs.title")}
              description={t("home.whatsHappening.installs.description")}
              emptyText={t("home.whatsHappening.installs.empty")}
              events={data?.installs}
              moreSearch={(e) =>
                storesListSearch({
                  onlyActive: false,
                  sortId: "installDate",
                  installFrom: e.windowFrom,
                  installTo: e.windowTo,
                })
              }
              {...shared}
            />,
          ],
          [
            <StoreEventsPanel
              key="goLives"
              title={t("home.whatsHappening.goLives.title")}
              description={t("home.whatsHappening.goLives.description")}
              emptyText={t("home.whatsHappening.goLives.empty")}
              events={data?.goLives}
              moreSearch={(e) =>
                storesListSearch({
                  onlyActive: false,
                  sortId: "goLiveDate",
                  goLiveFrom: e.windowFrom,
                  goLiveTo: e.windowTo,
                })
              }
              {...shared}
            />,
          ],
        ]}
      />
    </section>
  );
}
